import React, { useEffect } from 'react';
import Layout from "../temp/layout/Layout";
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import { 
    FaSchool, FaUniversity, FaChalkboardTeacher, FaChartLine,
    FaUsers, FaCheckCircle, FaArrowRight, FaGraduationCap,
    FaClock, FaTrendingUp
} from 'react-icons/fa';

const CaseStudies = () => {
    useEffect(() => {
        AOS.init({ 
            duration: 1000, 
            once: true,
            offset: 100
        });
    }, []);

    const caseStudies = [
        {
            id: 1,
            title: "Delhi Public School - Digital Transformation Success",
            institution: "Delhi Public School",
            type: "School",
            icon: <FaSchool className="text-4xl" />,
            challenge: "Managing 2000+ students with manual processes causing delays and errors",
            solution: "Implemented Educational Eternity ERP system with complete automation",
            results: [
                "80% reduction in administrative workload",
                "95% parent satisfaction with communication",
                "50% faster fee collection process",
                "Real-time attendance tracking with SMS alerts"
            ],
            metrics: [
                { label: "Students", value: "2000+" },
                { label: "Time Saved", value: "40 hrs/week" },
                { label: "Efficiency", value: "+80%" }
            ],
            color: "from-blue-500 to-indigo-600"
        },
        {
            id: 2,
            title: "St. Xavier's College - Streamlined Operations",
            institution: "St. Xavier's College",
            type: "College",
            icon: <FaUniversity className="text-4xl" />,
            challenge: "Complex semester system and course management requiring better organization",
            solution: "Deployed College Management Suite with advanced features",
            results: [
                "Automated semester and credit management",
                "Streamlined course registration process",
                "Improved student-faculty communication",
                "Enhanced research tracking and management"
            ],
            metrics: [
                { label: "Students", value: "5000+" },
                { label: "Courses", value: "150+" },
                { label: "Efficiency", value: "+75%" }
            ],
            color: "from-purple-500 to-pink-600"
        },
        {
            id: 3,
            title: "Elite Coaching Academy - Performance Tracking Excellence",
            institution: "Elite Coaching Academy",
            type: "Coaching",
            icon: <FaChalkboardTeacher className="text-4xl" />,
            challenge: "Tracking student performance across multiple batches and subjects",
            solution: "Implemented Coaching Institute Solutions with AI-powered analytics",
            results: [
                "Real-time performance tracking for all students",
                "Automated mock test scheduling and grading",
                "Personalized study recommendations",
                "Improved student engagement by 60%"
            ],
            metrics: [
                { label: "Students", value: "1200+" },
                { label: "Batches", value: "45+" },
                { label: "Success Rate", value: "+35%" }
            ],
            color: "from-green-500 to-teal-600"
        }
    ];

    return (
        <Layout>
            <Helmet>
                <title>Case Studies - Educational Eternity | Success Stories</title>
                <meta name="description" content="Explore real success stories and case studies from educational institutions using Educational Eternity ERP system." />
            </Helmet>

            {/* Hero Section */}
            <section className="relative w-full h-[50vh] bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `url(assets/images/home1.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}></div>
                <div className="relative z-10 text-center px-4">
                    <h1 data-aos="fade-up" className="text-5xl md:text-7xl font-bold text-white mb-4">
                        Case Studies
                    </h1>
                    <p data-aos="fade-up" data-aos-delay="100" className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                        Real success stories from institutions we've transformed
                    </p>
                </div>
            </section>

            {/* Case Studies Grid */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {caseStudies.map((study, idx) => (
                            <div
                                key={study.id}
                                data-aos="fade-up"
                                data-aos-delay={idx * 100}
                                className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
                            >
                                {/* Header */}
                                <div className={`bg-gradient-to-r ${study.color} p-6 text-white`}>
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="bg-white/20 backdrop-blur-md p-4 rounded-xl">
                                            {study.icon}
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold opacity-90">{study.type}</div>
                                            <div className="text-xl font-bold">{study.institution}</div>
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-bold">{study.title}</h3>
                                </div>

                                {/* Content */}
                                <div className="p-6">
                                    {/* Challenge */}
                                    <div className="mb-6">
                                        <h4 className="text-sm font-bold text-gray-500 uppercase mb-2">Challenge</h4>
                                        <p className="text-gray-700">{study.challenge}</p>
                                    </div>

                                    {/* Solution */}
                                    <div className="mb-6">
                                        <h4 className="text-sm font-bold text-gray-500 uppercase mb-2">Solution</h4>
                                        <p className="text-gray-700">{study.solution}</p>
                                    </div>

                                    {/* Results */}
                                    <div className="mb-6">
                                        <h4 className="text-sm font-bold text-gray-500 uppercase mb-3">Results</h4>
                                        <ul className="space-y-2">
                                            {study.results.map((result, i) => (
                                                <li key={i} className="flex items-start">
                                                    <FaCheckCircle className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                                                    <span className="text-gray-700 text-sm">{result}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Metrics */}
                                    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                                        {study.metrics.map((metric, i) => (
                                            <div key={i} className="text-center">
                                                <div className={`text-2xl font-bold bg-gradient-to-r ${study.color} bg-clip-text text-transparent`}>
                                                    {metric.value}
                                                </div>
                                                <div className="text-xs text-gray-600 mt-1">{metric.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
                <div className="container mx-auto px-4 text-center" data-aos="fade-up">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Write Your Success Story?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                        Join these institutions and transform your educational operations with Educational Eternity.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/contact">
                            <button className="px-8 py-4 bg-white text-blue-600 rounded-full text-lg font-bold shadow-2xl hover:shadow-white/50 transform hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto">
                                Get Started <FaArrowRight />
                            </button>
                        </Link>
                        <Link to="/plans">
                            <button className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full text-lg font-bold hover:bg-white hover:text-blue-600 transform hover:scale-105 transition-all duration-300">
                                View Plans
                            </button>
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default CaseStudies;

